import { createServerFn } from "@tanstack/react-start";

interface WeatherDay {
  date: string;
  tempHigh: number;
  tempLow: number;
  precipChance: number;
  condition: string;
  icon: string;
}

interface CurrentWeather {
  temp: number;
  condition: string;
  icon: string;
}

export interface WeatherForecast {
  days: WeatherDay[];
  summary: string;
  isRainy: boolean;
  precipChance: number;
  current?: CurrentWeather;
}

// In-memory cache (per worker instance). Lakewood Ranch forecast is identical
// for every user, so we can safely share it across requests for ~10 min.
const CACHE_TTL_MS = 10 * 60 * 1000;
const cache = new Map<string, { expires: number; value: WeatherForecast }>();

// NWS requires a descriptive User-Agent identifying the app + contact.
const NWS_HEADERS = {
  "User-Agent": "RanchPlanner/1.0 (https://ranchplanner.com, contact@1uplift.com)",
  Accept: "application/geo+json",
};

// Cache the NWS /points lookup essentially forever — the grid for a fixed
// lat/lng never changes.
let pointsCache: { forecastUrl: string; gridDataUrl: string; gridId: string } | null = null;
let stationsCache: { stationId: string } | null = null;
let observationStationsUrl: string | null = null;

async function fetchWithRetry(url: string, init: RequestInit = {}, retries = 2): Promise<Response> {
  let lastRes: Response | undefined;
  for (let attempt = 0; attempt <= retries; attempt++) {
    const res = await fetch(url, init);
    if (res.ok) return res;
    lastRes = res;
    // Only retry on 429 (rate limit) or 5xx
    if (res.status !== 429 && res.status < 500) return res;
    if (attempt < retries) {
      const backoff = 400 * Math.pow(2, attempt);
      await new Promise((r) => setTimeout(r, backoff));
    }
  }
  return lastRes!;
}

export const getWeatherForecast = createServerFn({ method: "POST" })
  .inputValidator((input: { startDate: string; endDate: string }) => input)
  .handler(async ({ data }): Promise<WeatherForecast> => {
    // Lakewood Ranch Main Street center
    const lat = 27.4015;
    const lng = -82.4070;

    const cacheKey = `${data.startDate}_${data.endDate}`;
    const cached = cache.get(cacheKey);
    if (cached && cached.expires > Date.now()) {
      return cached.value;
    }

    try {
      // Step 1: resolve the NWS grid forecast URL (cached after first call).
      if (!pointsCache) {
        const pointsRes = await fetchWithRetry(
          `https://api.weather.gov/points/${lat},${lng}`,
          { headers: NWS_HEADERS },
        );
        if (!pointsRes.ok) {
          console.error("NWS /points error:", pointsRes.status);
          if (cached) return cached.value;
          return fallback();
        }
        const pointsJson = await pointsRes.json();
        pointsCache = {
          forecastUrl: pointsJson.properties.forecast,
          gridDataUrl: pointsJson.properties.forecastGridData,
          gridId: pointsJson.properties.gridId,
        };
        observationStationsUrl = pointsJson.properties.observationStations;
      }

      // Step 2: fetch BOTH the narrative forecast (for shortForecast text +
      // "current" conditions) and the raw grid data (for true daily highs/lows
      // and precip probability that don't disappear as the day progresses).
      const [narrativeRes, gridRes] = await Promise.all([
        fetchWithRetry(pointsCache.forecastUrl, { headers: NWS_HEADERS }),
        fetchWithRetry(pointsCache.gridDataUrl, { headers: NWS_HEADERS }),
      ]);
      if (!narrativeRes.ok || !gridRes.ok) {
        console.error("NWS error:", narrativeRes.status, gridRes.status);
        pointsCache = null;
        if (cached) return cached.value;
        return fallback();
      }

      const narrativeJson = await narrativeRes.json();
      const gridJson = await gridRes.json();
      const periods: NwsPeriod[] = narrativeJson?.properties?.periods ?? [];
      if (periods.length === 0) return fallback();

      const days = gridDataToDays(gridJson?.properties, periods, data.startDate, data.endDate);
      if (days.length === 0) return fallback();

      // "Current" — prefer real observation from nearest station so we match
      // what consumer weather apps show. Fall back to the first forecast
      // period if observations are unavailable.
      const first = periods[0];
      let current: CurrentWeather = {
        temp: first.temperature,
        condition: shortForecastToCondition(first.shortForecast),
        icon: shortForecastToIcon(first.shortForecast, first.isDaytime),
      };
      try {
        if (!stationsCache && observationStationsUrl) {
          const sRes = await fetchWithRetry(observationStationsUrl, { headers: NWS_HEADERS });
          if (sRes.ok) {
            const sJson = await sRes.json();
            const id = sJson?.features?.[0]?.properties?.stationIdentifier;
            if (id) stationsCache = { stationId: id };
          }
        }
        if (stationsCache) {
          const oRes = await fetchWithRetry(
            `https://api.weather.gov/stations/${stationsCache.stationId}/observations/latest`,
            { headers: NWS_HEADERS },
          );
          if (oRes.ok) {
            const oJson = await oRes.json();
            const tempC = oJson?.properties?.temperature?.value;
            const desc = oJson?.properties?.textDescription as string | undefined;
            if (typeof tempC === "number") {
              current = {
                temp: cToF(tempC),
                condition: desc ? shortForecastToCondition(desc) : current.condition,
                icon: desc ? shortForecastToIcon(desc, true) : current.icon,
              };
            }
          }
        }
      } catch (e) {
        console.error("NWS observation fetch failed:", e);
      }

      const avgPrecip = days.reduce((sum, d) => sum + d.precipChance, 0) / days.length;
      // isRainy is kept for backward-compat consumers, but ranking logic now
      // uses the precipChance gradient directly.
      const isRainy = avgPrecip > 70;

      const summary = avgPrecip >= 80
        ? `Rain very likely (${Math.round(avgPrecip)}%) — leaning indoor for now.`
        : avgPrecip >= 50
          ? `Scattered showers possible (${Math.round(avgPrecip)}%) — Florida storms pass quickly, so we're keeping outdoor picks in the mix.`
          : avgPrecip > 20
            ? `Slight rain chance (${Math.round(avgPrecip)}%) — outdoor plans should be fine.`
            : `Clear skies ahead (${Math.round(avgPrecip)}% rain chance) — perfect for outdoor adventures!`;

      const value: WeatherForecast = {
        days,
        summary,
        isRainy,
        precipChance: Math.round(avgPrecip),
        current,
      };
      cache.set(cacheKey, { expires: Date.now() + CACHE_TTL_MS, value });
      return value;
    } catch (err) {
      console.error("Weather fetch failed:", err);
      if (cached) return cached.value;
      return fallback();
    }
  });

function fallback(): WeatherForecast {
  return {
    days: [],
    summary: "Weather data unavailable — showing all activities.",
    isRainy: false,
    precipChance: 0,
  };
}

// ---- NWS helpers ----

interface NwsPeriod {
  number: number;
  name: string;
  startTime: string; // ISO
  endTime: string;
  isDaytime: boolean;
  temperature: number; // already in Fahrenheit when units=us (default)
  shortForecast: string;
  probabilityOfPrecipitation?: { value: number | null };
}

interface GridSeriesPoint {
  validTime: string; // e.g. "2026-04-27T12:00:00+00:00/PT13H"
  value: number | null;
}
interface GridSeries {
  uom?: string;
  values?: GridSeriesPoint[];
}

function cToF(c: number): number {
  return Math.round((c * 9) / 5 + 32);
}

/** Local date bucket from a validTime ISO interval (use first segment). */
function bucketDate(validTime: string): string {
  return validTime.slice(0, 10);
}

/** Parse "PT13H" / "PT1H30M" into hours. */
function parseDurationHours(d: string): number {
  const m = d.match(/PT(?:(\d+)H)?(?:(\d+)M)?/);
  if (!m) return 1;
  const h = m[1] ? parseInt(m[1], 10) : 0;
  const min = m[2] ? parseInt(m[2], 10) : 0;
  return h + min / 60;
}

/**
 * Expand a series point into the set of local-date buckets it covers, so a
 * 24-hour POP block straddling midnight contributes to both days.
 */
function bucketsForPoint(point: GridSeriesPoint): string[] {
  const [startIso, dur] = point.validTime.split("/");
  const start = new Date(startIso);
  const hours = dur ? parseDurationHours(dur) : 1;
  const end = new Date(start.getTime() + hours * 3600_000);
  const buckets = new Set<string>();
  // Step in 1-hour increments using the original ISO offset baseline
  for (let t = start.getTime(); t < end.getTime(); t += 3600_000) {
    buckets.add(new Date(t).toISOString().slice(0, 10));
  }
  // Always include the nominal start bucket from the raw string (preserves
  // NWS's local-date intent even if UTC slicing shifts it).
  buckets.add(bucketDate(point.validTime));
  return Array.from(buckets);
}

/**
 * Build daily WeatherDay entries from the raw grid data. Uses narrative
 * periods only to source human-readable shortForecast text per date.
 */
function gridDataToDays(
  props: { maxTemperature?: GridSeries; minTemperature?: GridSeries; probabilityOfPrecipitation?: GridSeries } | undefined,
  periods: NwsPeriod[],
  startDate: string,
  endDate: string,
): WeatherDay[] {
  if (!props) return [];

  const highs = new Map<string, number>(); // date -> °F
  const lows = new Map<string, number>();
  const popByDate = new Map<string, number>();

  for (const p of props.maxTemperature?.values ?? []) {
    if (p.value == null) continue;
    const date = bucketDate(p.validTime);
    const f = cToF(p.value);
    const prev = highs.get(date);
    highs.set(date, prev == null ? f : Math.max(prev, f));
  }
  for (const p of props.minTemperature?.values ?? []) {
    if (p.value == null) continue;
    const date = bucketDate(p.validTime);
    const f = cToF(p.value);
    const prev = lows.get(date);
    lows.set(date, prev == null ? f : Math.min(prev, f));
  }
  for (const p of props.probabilityOfPrecipitation?.values ?? []) {
    if (p.value == null) continue;
    for (const date of bucketsForPoint(p)) {
      const prev = popByDate.get(date) ?? 0;
      if (p.value > prev) popByDate.set(date, p.value);
    }
  }

  // Map each date to its best narrative period (prefer daytime) for text/icon.
  const dayNarrative = new Map<string, NwsPeriod>();
  const nightNarrative = new Map<string, NwsPeriod>();
  for (const p of periods) {
    const date = p.startTime.slice(0, 10);
    if (p.isDaytime) {
      if (!dayNarrative.has(date)) dayNarrative.set(date, p);
    } else {
      if (!nightNarrative.has(date)) nightNarrative.set(date, p);
    }
  }

  // Union of dates that have at least one of high/low, within window.
  const allDates = new Set<string>([...highs.keys(), ...lows.keys()]);
  const dates = Array.from(allDates)
    .filter((d) => d >= startDate && d <= endDate)
    .sort();

  return dates.map((date) => {
    const high = highs.get(date);
    const low = lows.get(date);
    const narrative = dayNarrative.get(date) ?? nightNarrative.get(date);
    const shortForecast = narrative?.shortForecast ?? "";
    return {
      date,
      tempHigh: high ?? low ?? 0,
      tempLow: low ?? high ?? 0,
      precipChance: Math.round(popByDate.get(date) ?? 0),
      condition: shortForecastToCondition(shortForecast),
      icon: shortForecastToIcon(shortForecast, narrative?.isDaytime ?? true),
    };
  });
}

function shortForecastToCondition(s: string): string {
  const f = s.toLowerCase();
  if (f.includes("thunder")) return "Thunderstorm";
  if (f.includes("snow")) return "Snow";
  if (f.includes("freezing")) return "Freezing rain";
  if (f.includes("sleet")) return "Sleet";
  if (f.includes("shower")) return "Showers";
  if (f.includes("rain") || f.includes("drizzle")) return "Rain";
  if (f.includes("fog") || f.includes("haze") || f.includes("smoke")) return "Foggy";
  if (f.includes("partly") || f.includes("mostly cloudy") || f.includes("mostly sunny")) return "Partly cloudy";
  if (f.includes("cloud") || f.includes("overcast")) return "Cloudy";
  if (f.includes("clear") || f.includes("sunny") || f.includes("fair")) return "Clear";
  return s || "Unknown";
}

function shortForecastToIcon(s: string, isDaytime: boolean): string {
  const f = s.toLowerCase();
  if (f.includes("thunder")) return "⛈️";
  if (f.includes("snow")) return "❄️";
  if (f.includes("freezing") || f.includes("sleet")) return "🌧️";
  if (f.includes("shower") || f.includes("rain") || f.includes("drizzle")) return "🌧️";
  if (f.includes("fog") || f.includes("haze") || f.includes("smoke")) return "🌫️";
  if (f.includes("partly") || f.includes("mostly cloudy") || f.includes("mostly sunny")) return "⛅";
  if (f.includes("cloud") || f.includes("overcast")) return "☁️";
  if (f.includes("clear") || f.includes("sunny") || f.includes("fair")) return isDaytime ? "☀️" : "🌙";
  return "🌤️";
}
